import {
  type BoardSummary,
  MATERIAL_MAX_BYTES,
  type Material,
  type ViewContext,
  fileLabel,
  materialLink,
  shortDate,
  simOfUrl,
  simUrl,
  simsFor,
  useAuth,
  useBoardActions,
  useBoards,
  useClasses,
  useCourses,
  useCurrentContext,
  useMaterialActions,
  useMaterials,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { Link, useNavigate } from '@tanstack/react-router';
import { FileText, FlaskConical, Link2, type LucideIcon, Presentation } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorText, Spinner } from '../components/ui';
import {
  Block,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  PillIcon,
  SUBJECT_GRAD,
  SubjectArt,
  TaskArt,
  pillClass,
} from '../design/forms';
import { ArrowUpRightIcon, PlusIcon, XIcon } from '../design/icons';
import { IconTile } from '../design/ui';

/*
 * «Матеріали» in «Форми 2» (28.10). The student had a white panel for each subject in two columns —
 * most with one line and a tall empty half beside a long one. Now one block as «Домашні»: the subjects on
 * top as chips to narrow it, then each subject with its picture and its materials and boards as soft
 * tiles side by side — a kind icon (a simulation, a link, a file, a board), the name, the day; the whole
 * tile opens it. The teacher: the course chosen in the title's row, the materials on the left, on the
 * right what to add and the boards.
 */

/**
 * The page's title with its controls: beside the title on a wide screen, in a row under it on a phone
 * (beside the big title they would squeeze it).
 */
function TitleRow({ children, ...head }: Parameters<typeof PageHead>[0]) {
  return (
    <div className="flex flex-col gap-3">
      <PageHead {...head}>
        {children && (
          <span className="hidden flex-wrap items-center gap-2 lg:flex">{children}</span>
        )}
      </PageHead>
      {children && <div className="flex flex-wrap items-center gap-2 lg:hidden">{children}</div>}
    </div>
  );
}

/** the kinds of a material: a line icon on its tone's square */
const KIND: Record<
  'sim' | 'link' | 'file' | 'board',
  { tone: 'green' | 'blue' | 'orange' | 'violet'; icon: LucideIcon }
> = {
  sim: { tone: 'green', icon: FlaskConical },
  link: { tone: 'blue', icon: Link2 },
  file: { tone: 'orange', icon: FileText },
  board: { tone: 'violet', icon: Presentation },
};

const field =
  'w-full rounded-[14px] bg-white px-3.5 text-[15px] text-[var(--sh-ink)] ring-1 ring-[#E4E0D7] outline-none ring-inset placeholder:text-[rgb(31_26_53/0.42)] focus:ring-2 focus:ring-[var(--sh-dark)] disabled:opacity-50';

const selectClass =
  'h-10 max-w-full rounded-full bg-white px-4 text-[14px] font-medium text-[var(--sh-ink)] ring-1 ring-[#E4E0D7] outline-none ring-inset focus:ring-2 focus:ring-[var(--sh-dark)]';

/** The whole tile opens it: the link is stretched over the tile, the buttons stand above it. */
const stretched = 'after:absolute after:inset-0 after:rounded-[18px] after:content-[""]';

/** A small ringed button over a tile: remove. */
function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="relative z-10 inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-[#E4E0D7] transition-colors ring-inset hover:bg-[#FFECE4] hover:text-[#B4431C]"
      style={{ color: INK_SOFT }}
    >
      <XIcon size={13} weight="bold" aria-hidden />
    </button>
  );
}

/** The ↗ at the end of a tile that opens: quiet, it darkens on hover (a row, not a section). */
function OpenMark() {
  return (
    <ArrowUpRightIcon
      size={14}
      weight="bold"
      aria-hidden
      color={INK}
      className="shrink-0 opacity-35 transition duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100"
    />
  );
}

function MaterialRow({ m, onRemove }: { m: Material; onRemove?: () => void }) {
  const { t } = useTranslation();
  const { supabase } = useAuth();
  const [error, setError] = useState(false);
  const sim = simOfUrl(m.url);
  const kind = KIND[sim ? 'sim' : m.url ? 'link' : 'file'];
  const title = 'block text-left text-[15px] leading-5 font-medium line-clamp-2 outline-none';
  return (
    <li
      className="group relative flex min-w-0 items-center gap-3 rounded-[18px] p-3 transition-colors hover:bg-[#EFECE5]"
      style={{ backgroundColor: PAPER }}
    >
      <IconTile tone={kind.tone} icon={kind.icon} size={40} />
      <div className="min-w-0 flex-1">
        {sim ? (
          // a simulation of the lab opens inside the app, with its «Спробуй»
          <Link
            to="/lab/sim/$slug"
            params={{ slug: sim.slug }}
            className={`${title} ${stretched}`}
            style={{ color: INK }}
          >
            {m.title}
          </Link>
        ) : (
          <button
            type="button"
            className={`${title} ${stretched}`}
            style={{ color: INK }}
            onClick={() => {
              setError(false);
              // a new tab first: browsers block windows opened after an await
              const tab = window.open('', '_blank');
              if (tab) tab.opener = null;
              materialLink(supabase, m).then(
                (url) => {
                  if (tab) tab.location.href = url;
                  else window.location.href = url;
                },
                () => {
                  tab?.close();
                  setError(true);
                },
              );
            }}
          >
            {m.title}
          </button>
        )}
        <p className="mt-0.5 truncate text-[12.5px] leading-[18px]" style={{ color: INK_SOFT }}>
          {shortDate(m.createdAt.slice(0, 10))} ·{' '}
          {sim ? t('materials.sim') : m.url ? t('materials.link') : fileLabel(m)}
        </p>
        {error && <ErrorText>{t('common.error')}</ErrorText>}
      </div>
      {onRemove ? <RemoveButton label={t('materials.remove')} onClick={onRemove} /> : <OpenMark />}
    </li>
  );
}

function AddMaterial({ ctx, courseId }: { ctx: ViewContext; courseId: string }) {
  const { t } = useTranslation();
  const actions = useMaterialActions(ctx.schoolId, ctx.memberId);
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File>();
  const [error, setError] = useState<string>();
  const busy = actions.addFile.isPending || actions.addLink.isPending;
  const reset = () => {
    setTitle('');
    setUrl('');
    setFile(undefined);
  };
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        setError(undefined);
        if (file && file.size > MATERIAL_MAX_BYTES) return setError(t('materials.tooBig'));
        const done = file
          ? actions.addFile.mutateAsync({ courseId, title, file })
          : actions.addLink.mutateAsync({ courseId, title, url });
        done.then(reset, () => setError(t('common.error')));
      }}
    >
      <input
        aria-label={t('materials.titleLabel')}
        placeholder={t('materials.titleLabel')}
        maxLength={200}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className={`${field} h-11`}
      />
      <label className="block">
        <span className="mb-1.5 block text-[13px] font-medium" style={{ color: INK_SOFT }}>
          {t('materials.url')}
        </span>
        <input
          type="url"
          pattern="https://.*"
          disabled={file !== undefined}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className={`${field} h-11`}
        />
      </label>
      <label className="block">
        <span
          className="mb-1.5 block text-[13px] leading-[18px] font-medium"
          style={{ color: INK_SOFT }}
        >
          {t('materials.file')}
        </span>
        <input
          type="file"
          disabled={url !== ''}
          onChange={(e) => setFile(e.target.files?.[0])}
          className="w-full text-[13px] file:mr-3 file:h-9 file:cursor-pointer file:rounded-full file:border-0 file:bg-[#F5F3EF] file:px-4 file:text-[13px] file:font-medium file:text-[var(--sh-ink)] hover:file:bg-[#EFECE5] disabled:opacity-50"
          style={{ color: INK_SOFT }}
        />
      </label>
      <ErrorText>{error}</ErrorText>
      <div>
        <button
          type="submit"
          disabled={busy || (!file && !(url.startsWith('https://') && title.trim()))}
          className={`${pillClass('dark')} disabled:opacity-35`}
        >
          {busy ? t('materials.uploading') : file ? t('materials.addFile') : t('materials.addLink')}
          <PillIcon icon={PlusIcon} />
        </button>
      </div>
    </form>
  );
}

/** A simulation of the lab for the course's subject, in two clicks. */
function AddSim({
  ctx,
  courseId,
  subject,
}: {
  ctx: ViewContext;
  courseId: string;
  subject: string;
}) {
  const { t } = useTranslation();
  const actions = useMaterialActions(ctx.schoolId, ctx.memberId);
  const sims = simsFor({ subject });
  const [slug, setSlug] = useState('');
  const chosen = sims.find((s) => s.slug === slug) ?? sims[0];
  if (!chosen) return null;
  return (
    <div className="mt-5 flex flex-col gap-2.5 border-t pt-5" style={{ borderColor: LINE }}>
      <p className="flex items-center gap-2.5 text-[15px] font-medium" style={{ color: INK }}>
        <IconTile tone={KIND.sim.tone} icon={KIND.sim.icon} size={32} />
        {t('materials.addSim')}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label={t('materials.pickSim')}
          value={chosen.slug}
          onChange={(e) => setSlug(e.target.value)}
          className={`${field} h-11 min-w-0 flex-1 basis-48 pr-3`}
        >
          {sims.map((s) => (
            <option key={s.slug} value={s.slug}>
              {s.title}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={actions.addLink.isPending}
          onClick={() =>
            actions.addLink.mutate({ courseId, title: chosen.title, url: simUrl(chosen.slug) })
          }
          className={`${pillClass('dark')} disabled:opacity-35`}
        >
          {t('lab.add.button')}
          <PillIcon icon={PlusIcon} />
        </button>
      </div>
    </div>
  );
}

function BoardRow({
  b,
  actions,
}: {
  b: BoardSummary;
  actions?: ReturnType<typeof useBoardActions>;
}) {
  const { t } = useTranslation();
  const [confirm, setConfirm] = useState(false);
  return (
    <li
      className="group relative flex min-w-0 flex-wrap items-center gap-3 rounded-[18px] p-3 transition-colors hover:bg-[#EFECE5]"
      style={{ backgroundColor: PAPER }}
    >
      <IconTile tone={KIND.board.tone} icon={KIND.board.icon} size={40} />
      <div className="min-w-0 flex-1">
        <Link
          to="/board/$boardId"
          params={{ boardId: b.id }}
          className={`block text-[15px] leading-5 font-medium line-clamp-2 ${stretched}`}
          style={{ color: INK }}
        >
          {b.title}
        </Link>
        <p className="mt-0.5 truncate text-[12.5px] leading-[18px]" style={{ color: INK_SOFT }}>
          {shortDate(b.updatedAt.slice(0, 10))} ·{' '}
          {actions
            ? b.shared
              ? t('board.list.shared')
              : t('board.list.notShared')
            : t('board.button')}
        </p>
      </div>
      {actions ? (
        <span className="relative z-10 flex w-full flex-wrap items-center gap-1.5 pl-[52px]">
          <button
            type="button"
            className={`${pillClass('light', 'sm')} pr-3.5`}
            onClick={() => actions.update.mutate({ id: b.id, shared: !b.shared })}
          >
            {b.shared ? t('board.list.unshare') : t('board.list.share')}
          </button>
          {confirm ? (
            <button
              type="button"
              className="inline-flex h-9 items-center rounded-full px-3.5 text-[13px] font-medium"
              style={{ backgroundColor: '#FFECE4', color: '#B4431C' }}
              onClick={() => actions.remove.mutate(b)}
            >
              {t('board.list.removeSure')}
            </button>
          ) : (
            <RemoveButton label={t('board.list.remove')} onClick={() => setConfirm(true)} />
          )}
        </span>
      ) : (
        <OpenMark />
      )}
    </li>
  );
}

/** Boards of a course: the teacher prepares them here and opens them in an online lesson. */
function CourseBoards({ ctx, courseId }: { ctx: ViewContext; courseId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const boards = useBoards({ courseId });
  const actions = useBoardActions(ctx.schoolId, ctx.memberId);
  const [title, setTitle] = useState('');
  const [error, setError] = useState(false);
  return (
    <Block>
      <Head title={t('board.list.title')} count={(boards.data ?? []).length} />
      <form
        className="flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setError(false);
          actions.create.mutateAsync({ courseId, title: title.trim() }).then(
            (boardId) => void navigate({ to: '/board/$boardId', params: { boardId } }),
            () => setError(true),
          );
        }}
      >
        <input
          aria-label={t('board.list.titleLabel')}
          placeholder={t('board.list.titleLabel')}
          maxLength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className={`${field} h-11 min-w-0 flex-1 basis-40`}
        />
        <button
          type="submit"
          disabled={!title.trim() || actions.create.isPending}
          className={`${pillClass('dark')} disabled:opacity-35`}
        >
          {t('board.list.new')}
          <PillIcon icon={PlusIcon} />
        </button>
      </form>
      {error && <ErrorText>{t('common.error')}</ErrorText>}
      {boards.isPending ? (
        <Spinner />
      ) : (boards.data ?? []).length === 0 ? (
        <p className="mt-4 text-[14px]" style={{ color: INK_SOFT }}>
          {t('board.list.empty')}
        </p>
      ) : (
        <ul className="mt-4 flex flex-col gap-2">
          {(boards.data ?? []).map((b) => (
            <BoardRow key={b.id} b={b} actions={actions} />
          ))}
        </ul>
      )}
      <p className="mt-4 text-[12.5px] leading-[18px]" style={{ color: INK_FAINT }}>
        {t('board.list.hint')}
      </p>
    </Block>
  );
}

function StaffView({ ctx }: { ctx: Extract<ViewContext, { kind: 'teacher' | 'admin' }> }) {
  const { t } = useTranslation();
  const classes = useClasses(ctx.schoolId);
  const [classId, setClassId] = useState('');
  const klass = classId || classes.data?.[0]?.id || '';
  const courses = useCourses(
    ctx.kind === 'teacher' ? { teacherMemberId: ctx.memberId } : { classId: klass },
  );
  const [courseId, setCourseId] = useState('');
  const course = courseId || courses.data?.[0]?.id || '';
  const materials = useMaterials({ courseId: course });
  const actions = useMaterialActions(ctx.schoolId, ctx.memberId);
  const subject = (courses.data ?? []).find((c) => c.id === course)?.subject ?? '';
  const list = materials.data ?? [];
  return (
    <div className="flex flex-col gap-4">
      <TitleRow title={t('materials.title')}>
        {ctx.kind === 'admin' && (
          <select
            aria-label={t('materials.course')}
            className={selectClass}
            value={klass}
            onChange={(e) => {
              setClassId(e.target.value);
              setCourseId('');
            }}
          >
            {(classes.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        )}
        <select
          aria-label={t('materials.course')}
          className={selectClass}
          value={course}
          onChange={(e) => setCourseId(e.target.value)}
        >
          {(courses.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.subject}
              {c.groupLabel ? ` (${c.groupLabel})` : ''} · {c.classLabel}
            </option>
          ))}
        </select>
      </TitleRow>
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(320px,400px)]">
        <Block>
          <Head title={t('nav.materials')} count={list.length}>
            {subject && (
              <SubjectArt
                icon={subjectLook(subject).icon}
                tone={SUBJECT_GRAD[subjectLook(subject).tone]}
                size={40}
              />
            )}
          </Head>
          {materials.isPending ? (
            <Spinner />
          ) : list.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-center">
              <TaskArt kind="soon" tone="sky" size={80} />
              <p className="mt-3 text-[15px]" style={{ color: INK_SOFT }}>
                {t('materials.empty')}
              </p>
            </div>
          ) : (
            <ul className="grid grid-cols-1 gap-2 2xl:grid-cols-2">
              {list.map((m) => (
                <MaterialRow key={m.id} m={m} onRemove={() => actions.remove.mutate(m)} />
              ))}
            </ul>
          )}
          <p className="mt-4 text-[12.5px] leading-[18px]" style={{ color: INK_FAINT }}>
            {t('materials.hint')}
          </p>
        </Block>
        {course && (
          <div className="flex min-w-0 flex-col gap-4">
            <Block>
              <Head title={t('materials.add')} />
              <AddMaterial ctx={ctx} courseId={course} />
              <AddSim ctx={ctx} courseId={course} subject={subject} />
            </Block>
            <CourseBoards ctx={ctx} courseId={course} />
          </div>
        )}
      </div>
    </div>
  );
}

function FamilyView({ ctx }: { ctx: Extract<ViewContext, { kind: 'student' | 'guardian' }> }) {
  const { t } = useTranslation();
  const materials = useMaterials({ schoolId: ctx.schoolId });
  const courses = useCourses({ classId: ctx.child.class?.id ?? '' });
  const [picked, setPicked] = useState<string>();
  const mine = new Set((courses.data ?? []).map((c) => c.id));
  // the boards the teachers showed in lessons sit with their subject's materials
  const boards = (useBoards({ schoolId: ctx.schoolId }).data ?? []).filter((b) =>
    mine.has(b.courseId),
  );
  const bySubject = new Map<string, { materials: Material[]; boards: BoardSummary[] }>();
  const of = (subject: string) => {
    const got = bySubject.get(subject) ?? { materials: [], boards: [] };
    bySubject.set(subject, got);
    return got;
  };
  for (const m of materials.data ?? []) if (mine.has(m.courseId)) of(m.subject).materials.push(m);
  for (const b of boards) of(b.subject).boards.push(b);
  const subjects = [...bySubject.keys()].sort((a, b) => a.localeCompare(b, 'uk'));
  const sizeOf = (s: string) =>
    (bySubject.get(s)?.materials.length ?? 0) + (bySubject.get(s)?.boards.length ?? 0);
  const total = subjects.reduce((n, s) => n + sizeOf(s), 0);
  const shown = picked && bySubject.has(picked) ? [picked] : subjects;
  const chip = (on: boolean) =>
    `inline-flex h-10 shrink-0 items-center gap-2 rounded-full pr-3.5 text-[14px] transition-colors ${on ? 'font-medium' : 'hover:bg-[#EFECE5]'}`;

  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('materials.title')}
        note={total > 0 ? t('catchUp.materials', { count: total }) : undefined}
      />
      <Block>
        {materials.isPending ? (
          <div
            className="h-[320px] animate-pulse rounded-[22px]"
            style={{ backgroundColor: PAPER }}
            aria-busy
          />
        ) : subjects.length === 0 ? (
          <div className="flex flex-col items-center py-14 text-center">
            <TaskArt kind="soon" tone="sky" size={96} />
            <p className="mt-3 font-display text-[26px] font-light" style={{ color: INK }}>
              {t('materials.empty')}
            </p>
          </div>
        ) : (
          <>
            {/* the subjects as chips: narrow the page to one */}
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                aria-pressed={!picked}
                onClick={() => setPicked(undefined)}
                className={`${chip(!picked)} pl-4`}
                style={{ backgroundColor: !picked ? '#E9E5DC' : PAPER, color: INK }}
              >
                {t('studentHome.shapes2.allSubjects')}
                <span style={{ color: INK_FAINT }}>{total}</span>
              </button>
              {subjects.map((s) => {
                const l = subjectLook(s);
                const on = s === picked;
                return (
                  <button
                    key={s}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setPicked(on ? undefined : s)}
                    className={`${chip(on)} pl-1.5`}
                    style={{ backgroundColor: on ? '#E9E5DC' : PAPER, color: INK }}
                  >
                    <SubjectArt icon={l.icon} tone={SUBJECT_GRAD[l.tone]} size={28} />
                    {s}
                    <span style={{ color: INK_FAINT }}>{sizeOf(s)}</span>
                  </button>
                );
              })}
            </div>

            {/* all subjects: their groups flow in columns, so a subject of one line leaves no hole;
                one subject: its tiles side by side */}
            <div
              className={picked ? 'mt-7' : 'mt-7 -mb-7 columns-1 gap-x-6 md:columns-2 xl:columns-3'}
            >
              {shown.map((s) => {
                const l = subjectLook(s);
                const group = bySubject.get(s) ?? { materials: [], boards: [] };
                return (
                  <section key={s} className={picked ? '' : 'mb-7 break-inside-avoid'}>
                    <h2
                      className="mb-3 flex items-center gap-3 font-display text-[24px] leading-8 font-light tracking-[-0.03em]"
                      style={{ color: INK }}
                    >
                      <SubjectArt icon={l.icon} tone={SUBJECT_GRAD[l.tone]} size={36} />
                      {s}
                      <span
                        className="font-sans text-[13px] tracking-normal"
                        style={{ color: INK_FAINT }}
                      >
                        {sizeOf(s)}
                      </span>
                    </h2>
                    <ul
                      className={
                        picked
                          ? 'grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3'
                          : 'flex flex-col gap-2'
                      }
                    >
                      {group.boards.map((b) => (
                        <BoardRow key={b.id} b={b} />
                      ))}
                      {group.materials.map((m) => (
                        <MaterialRow key={m.id} m={m} />
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>
          </>
        )}
      </Block>
    </div>
  );
}

export function MaterialsPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  return ctx.kind === 'student' || ctx.kind === 'guardian' ? (
    <FamilyView ctx={ctx} />
  ) : ctx.kind === 'psychologist' ? (
    <PageHead title={t('materials.title')} />
  ) : (
    <StaffView ctx={ctx} />
  );
}
